import * as React from 'react';
import { View, Text } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import PDFInsuranceBillItemsTable from '../base/PDFInsuranceBillItemsTable';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { formatDateForPDF } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { InsuranceBill, PaymentRecord, DocumentType } from '../../../pages/hospital/Billing/types';
import { documentTypeToString } from '../../../utils/bill';
import { calculateTotalBill } from '../../../utils/bill';
import { TYPE_BILL_ITEM } from '../../../constants/types';

/**
 * Insurance Bill PDF Document Component
 * 
 * Renders insurance bills in PDF format.
 * Insurance bills aggregate multiple patient claims for a single insurance organization.
 * 
 * Features:
 * - Insurance organization information (instead of patient info)
 * - Bill items table (representing claims)
 * - Payment summary
 * - Totals and summary information
 */

interface InsuranceBillPDFDocumentProps {
  bill: InsuranceBill;
  currency: string;
  locale?: string;
  config: PDFConfig;
  paymentRecords?: PaymentRecord[];
}

const formatCurrency = (amount: number, currency: string, locale: string): string => {
  // Replace non-breaking spaces and other special space characters with regular spaces
  const formatted = new Intl.NumberFormat(locale).format(amount).replace(/[\u00A0\u202F\u2009]/g, ' ');
  return `${formatted} ${currency}`;
};

export const InsuranceBillPDFDocument: React.FC<InsuranceBillPDFDocumentProps> = ({
  bill,
  currency,
  locale = 'fr',
  config,
  paymentRecords,
}) => {
  const t = createT(locale);

  // Calculate totals
  const totalBill = React.useMemo(() => {
    if (bill.billItems && bill.billItems.length > 0) {
      return calculateTotalBill(bill.billItems, [TYPE_BILL_ITEM.HIDDEN_VALUE, TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO]);
    }
    return bill.totalGross || 0;
  }, [bill.billItems, bill.totalGross]);

  const totalPaidFromRecords = React.useMemo(() => {
    return paymentRecords ? paymentRecords.reduce((sum, record) => sum + record.amount, 0) : 0;
  }, [paymentRecords]);

  const totalPaid = bill.totalPaid || totalPaidFromRecords;
  const totalDiscount = bill.totalDiscount || 0;
  const remainingAmount = totalBill - totalPaid - totalDiscount;

  // Get coverage percentage from bill if available
  const coveragePercentage = (bill as any).coveragePercentage ?? null;

  // Render insurance organization info (replaces patient info)
  const renderInsuranceInfo = (): React.ReactNode => (
    <View style={{ flexDirection: 'row', width: '100%', marginBottom: 10 }}>
      <View style={{ width: '40%', paddingRight: 10 }}>
        <View style={{ marginBottom: 5 }}>
          <Text style={specializedTypography.fieldLabel}>
            {t('hospital.billing.insurance_bills.insurance_bill.organization')}: 
          </Text>
          <Text style={specializedTypography.fieldValue}>
            {bill.organizationName || '-'}
            {coveragePercentage !== null && ` (${coveragePercentage}%)`}
          </Text>
        </View>
      </View>
      <View style={{ width: '40%', paddingLeft: 10 }}>
        <View style={{ marginBottom: 5 }}>
          <Text style={specializedTypography.fieldLabel}>
            {t('hospital.billing.bill.insurance.bill_id')}:
          </Text>
          <Text style={specializedTypography.fieldValue}>
            {(bill as any).idInvestigation || (bill as any).documentNumber || (bill as any).id || bill.uuid || '-'}
          </Text>
        </View>
      </View>
      <View style={{ width: '20%', paddingLeft: 10 }}>
        <View style={{ marginBottom: 5 }}>
          <Text style={specializedTypography.fieldLabel}>
            {t('general.date')}:
          </Text>
          <Text style={specializedTypography.fieldValue}>
            {formatDateForPDF(bill.createdAt, locale)}
          </Text>
        </View>
      </View>
    </View>
  );

  // Render title based on document type
  const renderTitle = (): React.ReactNode => (
    <Text style={typographyStyles.h2}>
      {t(`hospital.billing.pdf.title.${documentTypeToString(bill.type)}`)}
    </Text>
  );

  // Render subtitle
  const renderSubtitle = (): React.ReactNode => (
    <View style={{ marginBottom: 10 }}>
      <Text style={typographyStyles.h6}>
        {t('hospital.billing.insurance_bills.insurance_bill.subtitle')}
      </Text>
    </View>
  );

  // Render summary information
  const renderSummary = (): React.ReactNode => {
    return (
      <View style={{ marginBottom: 10 }}>
        <Text style={typographyStyles.body2}>
          <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.total_cost')}</Text>: {formatCurrency(totalBill, currency, locale)}
        </Text>
        
        {totalDiscount > 0 && (
          <Text style={typographyStyles.body2}>
            <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.discount')}</Text>: {formatCurrency(totalDiscount, currency, locale)}
          </Text>
        )}
        
        <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
          <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.insurance.total_paid')}</Text>: {formatCurrency(totalPaid, currency, locale)}
        </Text>
        
        <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
          <Text style={{ fontWeight: 'bold' }}>{t('hospital.billing.pdf.insurance.to_pay')}</Text>: {formatCurrency(Math.max(0, remainingAmount), currency, locale)}
        </Text>
      </View>
    );
  };

  // Render payment summary table
  const renderPaymentSummaryTable = (): React.ReactNode => {
    if (!paymentRecords || paymentRecords.length === 0) {
      return null;
    }

    return (
      <View>
        <Text style={[typographyStyles.h6, { fontWeight: 'bold', marginBottom: 5 }]}>
          {t('pages.hospital.billing.pdf.insurance.payment_summary')}
        </Text>
        
        <View style={{ backgroundColor: '#f9f9f9', padding: 5, borderTopWidth: 1, borderTopColor: '#ddd' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
              {t('pages.hospital.billing.pdf.insurance.total_paid_history')}
            </Text>
            <Text style={[typographyStyles.body2, { fontWeight: 'bold' }]}>
              {formatCurrency(totalPaidFromRecords, currency, locale)}
            </Text>
          </View>
        </View>
        
        <View style={{ backgroundColor: '#e8f5e8', padding: 5, borderTopWidth: 1, borderTopColor: '#ddd' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={[typographyStyles.body2, { fontWeight: 'bold', color: remainingAmount > 0 ? '#d32f2f' : '#2e7d32' }]}>
              {t('pages.hospital.billing.pdf.insurance.remaining_amount')}
            </Text>
            <Text style={[typographyStyles.body2, { fontWeight: 'bold', color: remainingAmount > 0 ? '#d32f2f' : '#2e7d32' }]}>
              {formatCurrency(Math.abs(remainingAmount), currency, locale)}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <PDFDocument
      title={t(`hospital.billing.pdf.title.${documentTypeToString(bill.type)}`)}
      patientInfo={renderInsuranceInfo()}
      config={config}
    >
      {/* Subtitle */}
      {renderSubtitle()}
      
      {/* Summary content */}
      {renderSummary()}
      
      {/* Bill Items Table - Custom table matching InsuranceBillView columns */}
      {bill.billItems && bill.billItems.length > 0 ? (
        <PDFInsuranceBillItemsTable 
          items={bill.billItems as any} 
          currency={currency} 
          locale={locale}
        />
      ) : (
        <View style={{ padding: 20, alignItems: 'center' }}>
          <Text style={typographyStyles.body1}>
            {t('hospital.billing.insurance_bills.insurance_bill.no_items')}
          </Text>
        </View>
      )}
      
      {/* Payment Summary */}
      {renderPaymentSummaryTable()}
    </PDFDocument>
  );
};

export default InsuranceBillPDFDocument;

